// TagGroup — an interactive collection of removable <Tag> chips on the React
// Aria foundation (ADR-029), per COMPONENTS §Catalog. Use it wherever the user
// can REMOVE a selection: the chips under a pick-many picker
// (selectedDisplay="tags"), future filter bars. For read-only chips use the
// static <Tag> — same visual recipe (shared `tagClass`), zero interaction.
//
// React Aria owns the keyboard model: arrow keys rove between chips, Backspace/
// Delete removes the focused chip, each X is a real labelled button, and screen
// readers announce each chip's position + removability. One X PER chip; a
// "clear everything" affordance is the owning field's `clearable` X — the two
// jobs never share a control.

import {
  TagGroup as AriaTagGroup,
  TagList,
  Tag as AriaTag,
  Button as AriaButton,
} from 'react-aria-components';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';
import { tagClass, type TagVariant } from './Tag.js';

export interface TagGroupItem {
  id: string;
  label: string;
}

export interface TagGroupProps {
  items: TagGroupItem[];
  /** Removes are enabled only when provided (X per chip + Backspace/Delete). */
  onRemove?: (id: string) => void;
  /** Accessible name for the group (WCAG 4.1.2). */
  ariaLabel: string;
  variant?: TagVariant;
  disabled?: boolean;
  className?: string;
}

export function TagGroup({
  items,
  onRemove,
  ariaLabel,
  variant = 'neutral',
  disabled = false,
  className = '',
}: TagGroupProps) {
  const { t } = useTranslation('common');

  return (
    <AriaTagGroup
      aria-label={ariaLabel}
      onRemove={onRemove ? (keys) => keys.forEach((k) => onRemove(String(k))) : undefined}
      className={className}
    >
      <TagList items={items} className="flex flex-wrap gap-1.5">
        {(item) => (
          <AriaTag
            id={item.id}
            textValue={item.label}
            isDisabled={disabled}
            className={
              `${tagClass(variant)} cursor-default outline-none ` +
              'focus-ring ' +
              'data-[disabled]:opacity-50'
            }
          >
            {item.label}
            {onRemove && (
              <AriaButton
                slot="remove"
                aria-label={t('tag.remove')}
                // 24px min target (SC 2.5.8) without growing the chip: the
                // button overshoots the chip's padding vertically.
                className={
                  'min-w-6 min-h-6 -my-1 -me-1.5 inline-flex items-center justify-center rounded-full cursor-pointer ' +
                  'text-[var(--color-text-secondary)] data-[hovered]:text-[var(--color-text-primary)] data-[hovered]:bg-[var(--color-bg-page)] data-[pressed]:opacity-70 ' +
                  'outline-none focus-ring-inset data-[disabled]:cursor-not-allowed'
                }
              >
                <icons.close aria-hidden size={12} />
              </AriaButton>
            )}
          </AriaTag>
        )}
      </TagList>
    </AriaTagGroup>
  );
}
